import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sparkles, Bot, Send, Copy, Megaphone, Briefcase, GraduationCap, Loader2, User2, Wand2 } from "lucide-react";
import { toast } from "sonner";

const copy = (t) => { navigator.clipboard.writeText(t || ""); toast.success("Copied to clipboard"); };

export default function AiStudio() {
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white flex items-center justify-center shadow-lg shadow-indigo-200"><Sparkles size={24} /></div>
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">AI Studio</h1>
          <p className="text-slate-500 text-sm">Powered by ChatGPT (GPT-5.6 Terra) — draft messages, screen applicants & get instant answers</p>
        </div>
      </div>

      <Tabs defaultValue="assistant">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="assistant" className="gap-1.5" data-testid="ai-tab-assistant"><Bot size={15} />Assistant</TabsTrigger>
          <TabsTrigger value="writer" className="gap-1.5" data-testid="ai-tab-writer"><Megaphone size={15} />Reminder & Notice</TabsTrigger>
          <TabsTrigger value="inquiry" className="gap-1.5" data-testid="ai-tab-inquiry"><Briefcase size={15} />Inquiry Helper</TabsTrigger>
          <TabsTrigger value="remarks" className="gap-1.5" data-testid="ai-tab-remarks"><GraduationCap size={15} />Remark Generator</TabsTrigger>
        </TabsList>
        <TabsContent value="assistant" className="mt-4"><AssistantTab /></TabsContent>
        <TabsContent value="writer" className="mt-4"><WriterTab /></TabsContent>
        <TabsContent value="inquiry" className="mt-4"><InquiryTab /></TabsContent>
        <TabsContent value="remarks" className="mt-4"><RemarksTab /></TabsContent>
      </Tabs>
    </div>
  );
}

const card = "bg-white rounded-2xl border border-slate-200 p-5 shadow-sm";
const Result = ({ text, testid }) => !text ? null : (
  <div className="relative mt-4">
    <Textarea readOnly value={text} rows={Math.min(14, Math.max(5, text.split("\n").length + 2))} className="bg-slate-50 text-slate-800 pr-12" data-testid={testid} />
    <Button size="sm" variant="outline" className="absolute top-2 right-2 h-8 gap-1" onClick={() => copy(text)} data-testid={`${testid}-copy`}><Copy size={13} />Copy</Button>
  </div>
);

function AssistantTab() {
  const [sid] = useState(() => localStorage.getItem("ai_sid") || (() => { const s = `ai-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem("ai_sid", s); return s; })());
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { api.get(`/ai/chat/${sid}`).then((r) => setMsgs(r.data)).catch(() => {}); }, [sid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const r = await api.post("/ai/chat", { session_id: sid, message: text });
      setMsgs((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); setMsgs((m) => m.slice(0, -1)); setInput(text); }
    finally { setBusy(false); }
  };
  const newChat = () => { const s = `ai-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem("ai_sid", s); window.location.reload(); };
  const suggestions = ["How many students have pending fees?", "Draft a short notice for the annual sports day", "How many staff job inquiries do we have?"];

  return (
    <div className={`${card} flex flex-col h-[calc(100vh-290px)] min-h-[420px] p-0 overflow-hidden`}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
        <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Bot size={16} className="text-indigo-600" />School Assistant</span>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={newChat} data-testid="ai-new-chat">New chat</Button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-chat-messages">
        {msgs.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center text-slate-400 gap-3">
            <Bot size={40} className="opacity-40" />
            <p className="text-sm">Ask me anything about your school or get help drafting text.</p>
            <div className="flex flex-wrap gap-2 justify-center max-w-lg">
              {suggestions.map((s) => <button key={s} onClick={() => setInput(s)} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-600" data-testid="ai-suggestion">{s}</button>)}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0"><Bot size={15} /></div>}
            <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-indigo-600 text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`} data-testid={`ai-msg-${m.role}`}>{m.content}</div>
            {m.role === "user" && <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center shrink-0"><User2 size={15} /></div>}
          </div>
        ))}
        {busy && <div className="flex gap-2.5"><div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center"><Bot size={15} /></div><div className="bg-slate-100 rounded-2xl px-3.5 py-2.5 rounded-bl-sm"><Loader2 size={15} className="animate-spin text-slate-400" /></div></div>}
        <div ref={endRef} />
      </div>
      <div className="border-t border-slate-100 p-3 flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type your message…" disabled={busy} data-testid="ai-chat-input" />
        <Button onClick={send} disabled={busy || !input.trim()} className="gap-1.5" data-testid="ai-chat-send"><Send size={15} />Send</Button>
      </div>
    </div>
  );
}

function WriterTab() {
  const [mode, setMode] = useState("reminder");
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [channel, setChannel] = useState("sms");
  const [tone, setTone] = useState("polite");
  const [extra, setExtra] = useState("");
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("parents");
  const [ntone, setNtone] = useState("formal");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/due-fees").then((r) => setStudents(r.data)).catch(() => {}); }, []);

  const gen = async () => {
    setBusy(true); setOut("");
    try {
      if (mode === "reminder") {
        const r = await api.post("/ai/reminder", { student_id: studentId || null, channel, tone, extra });
        setOut(r.data.text);
      } else {
        if (!topic.trim()) { toast.error("Enter a notice topic"); setBusy(false); return; }
        const r = await api.post("/ai/notice", { topic, audience, tone: ntone });
        setOut(r.data.text);
      }
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className={card}>
      <div className="inline-flex rounded-xl border border-slate-200 p-1 mb-4">
        {[["reminder", "Fee Reminder"], ["notice", "Parent Notice"]].map(([k, l]) => (
          <button key={k} onClick={() => { setMode(k); setOut(""); }} className={`px-4 py-1.5 rounded-lg text-sm font-semibold ${mode === k ? "bg-indigo-600 text-white" : "text-slate-500"}`} data-testid={`ai-writer-${k}`}>{l}</button>
        ))}
      </div>
      {mode === "reminder" ? (
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs">Student (optional — adds due details)</Label>
            <Select value={studentId || "none"} onValueChange={(v) => setStudentId(v === "none" ? "" : v)}><SelectTrigger data-testid="ai-rem-student"><SelectValue placeholder="Generic reminder" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Generic (no specific student)</SelectItem>{students.map((s) => <SelectItem key={s.student_id} value={s.student_id}>{s.name} — {s.class_name} (Due ₹{Math.round(s.due)})</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs">Channel</Label>
            <Select value={channel} onValueChange={setChannel}><SelectTrigger data-testid="ai-rem-channel"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="sms">SMS</SelectItem><SelectItem value="whatsapp">WhatsApp</SelectItem></SelectContent></Select>
          </div>
          <div><Label className="text-xs">Tone</Label>
            <Select value={tone} onValueChange={setTone}><SelectTrigger data-testid="ai-rem-tone"><SelectValue /></SelectTrigger><SelectContent>{["polite", "friendly", "firm", "urgent"].map((t) => <SelectItem key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="sm:col-span-2"><Label className="text-xs">Extra instructions (optional)</Label><Input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="e.g. mention last date is 10th" data-testid="ai-rem-extra" /></div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label className="text-xs">Notice Topic *</Label><Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. School closed on 2nd Oct for Gandhi Jayanti" data-testid="ai-notice-topic" /></div>
          <div><Label className="text-xs">Audience</Label>
            <Select value={audience} onValueChange={setAudience}><SelectTrigger data-testid="ai-notice-audience"><SelectValue /></SelectTrigger><SelectContent>{["parents", "students", "staff", "all"].map((a) => <SelectItem key={a} value={a}>{a[0].toUpperCase() + a.slice(1)}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs">Tone</Label>
            <Select value={ntone} onValueChange={setNtone}><SelectTrigger data-testid="ai-notice-tone"><SelectValue /></SelectTrigger><SelectContent>{["formal", "friendly", "celebratory"].map((t) => <SelectItem key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
      )}
      <Button className="mt-4 gap-1.5" onClick={gen} disabled={busy} data-testid="ai-writer-generate">{busy ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />}Generate</Button>
      <Result text={out} testid="ai-writer-result" />
    </div>
  );
}

function InquiryTab() {
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState("");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/staff-leads").then((r) => setLeads(r.data)).catch(() => {}); }, []);
  const gen = async () => {
    if (!leadId) { toast.error("Select an applicant"); return; }
    setBusy(true); setOut("");
    try { const r = await api.post("/ai/inquiry-summary", { lead_id: leadId }); setOut(r.data.text); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className={card}>
      <p className="text-sm text-slate-500 mb-3">Pick a staff job applicant to get an AI summary, tailored interview questions and a shortlist recommendation.</p>
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[240px]"><Label className="text-xs">Applicant</Label>
          <Select value={leadId} onValueChange={setLeadId}><SelectTrigger data-testid="ai-inq-lead"><SelectValue placeholder="Select an applicant" /></SelectTrigger>
            <SelectContent>{leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}{l.post_applied_for ? ` — ${l.post_applied_for}` : ""}</SelectItem>)}</SelectContent></Select>
        </div>
        <Button className="gap-1.5" onClick={gen} disabled={busy} data-testid="ai-inq-generate">{busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}Analyze</Button>
      </div>
      <Result text={out} testid="ai-inq-result" />
    </div>
  );
}

function RemarksTab() {
  const [name, setName] = useState("");
  const [perf, setPerf] = useState("");
  const [tone, setTone] = useState("encouraging");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    setBusy(true); setOut("");
    try { const r = await api.post("/ai/remarks", { student_name: name, performance: perf, tone }); setOut(r.data.text); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className={card}>
      <p className="text-sm text-slate-500 mb-3">Enter a student's name and a quick summary of their marks/performance to auto-write a polished report-card remark.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label className="text-xs">Student Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Aarav" data-testid="ai-rmk-name" /></div>
        <div><Label className="text-xs">Tone</Label>
          <Select value={tone} onValueChange={setTone}><SelectTrigger data-testid="ai-rmk-tone"><SelectValue /></SelectTrigger><SelectContent>{["encouraging", "formal", "balanced", "motivational"].map((t) => <SelectItem key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="sm:col-span-2"><Label className="text-xs">Performance / Marks Summary</Label><Textarea rows={3} value={perf} onChange={(e) => setPerf(e.target.value)} placeholder="e.g. Scored 85% overall. Excellent in Science & Maths, needs improvement in Hindi. Active in class." data-testid="ai-rmk-perf" /></div>
      </div>
      <Button className="mt-4 gap-1.5" onClick={gen} disabled={busy} data-testid="ai-rmk-generate">{busy ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />}Generate Remark</Button>
      <Result text={out} testid="ai-rmk-result" />
    </div>
  );
}
